import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, type LucideIcon } from "lucide-react";
import PageHead from "@/components/PageHead";
import AppShell from "@/components/shell/AppShell";

export type HubLink = { to: string; label: string; hint: string; icon: LucideIcon };
export type HubGroup = { title: string; links: HubLink[] };

type Props = {
  title: string;
  intro: string;
  path: string;
  groups: HubGroup[];
  /** Extra blocks rendered after the link groups (history, saved careers…). */
  children?: ReactNode;
  top?: ReactNode;
};

/**
 * One pattern for every hub: a calm header, grouped list rows, one tap to
 * anywhere. Presentation only — each row is a plain link to an existing page.
 */
export default function HubPage({ title, intro, path, groups, children, top }: Props) {
  return (
    <AppShell title={title}>
      <PageHead title={`${title} | WeKIT Career Clarity 360™`} description={intro} path={path} noIndex />
      <header className="mb-8">
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{title}</h1>
        <p className="mt-2 max-w-prose text-base text-muted-foreground">{intro}</p>
      </header>
      {top && <div className="mb-8">{top}</div>}
      <div className="space-y-8">
        {groups.map((g) => (
          <section key={g.title} aria-labelledby={`hub-${g.title}`}>
            <h2
              id={`hub-${g.title}`}
              className="mb-3 px-1 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground"
            >
              {g.title}
            </h2>
            <ul className="overflow-hidden rounded-3xl border border-border/50 bg-card/50 divide-y divide-border/40">
              {g.links.map((l) => (
                <li key={l.to + l.label}>
                  <Link
                    to={l.to}
                    className="flex min-h-[64px] items-center gap-4 px-5 py-3 transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                  >
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-primary/12 text-primary">
                      <l.icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[15px] font-medium">{l.label}</span>
                      <span className="block truncate text-sm text-muted-foreground">{l.hint}</span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
        {children}
      </div>
    </AppShell>
  );
}
